import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import Button from 'react-bootstrap/Button'

import { getSeatmapTutorialSteps } from './seatmapTutorialSteps'

import './seatmapTutorial.scss'

const PADDING = 8
const VIEWPORT_MARGIN = 16

function getTargetRect(selector) {
  if (!selector) return null
  const el = document.querySelector(`[data-seatmap-tour="${selector}"]`)
  if (!el) return null
  const rect = el.getBoundingClientRect()
  return {
    top: rect.top - PADDING,
    left: rect.left - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  }
}

function getCardPosition(rect, placement) {
  if (!rect || placement === 'center') {
    return { className: 'seatmap-tutorial__card--center' }
  }

  const cardWidth = Math.min(360, window.innerWidth - 32)
  const cardHeight = 220
  let top
  let left

  switch (placement) {
    case 'right':
      top = rect.top + rect.height / 2 - cardHeight / 2
      left = rect.left + rect.width + 16
      break
    case 'left':
      top = rect.top + rect.height / 2 - cardHeight / 2
      left = rect.left - cardWidth - 16
      break
    case 'bottom':
      top = rect.top + rect.height + 16
      left = rect.left + rect.width / 2 - cardWidth / 2
      break
    default:
      top = rect.top - cardHeight - 16
      left = rect.left + rect.width / 2 - cardWidth / 2
  }

  top = Math.max(
    VIEWPORT_MARGIN,
    Math.min(top, window.innerHeight - cardHeight - VIEWPORT_MARGIN)
  )
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - cardWidth - VIEWPORT_MARGIN)
  )

  return {
    className: '',
    style: { top: `${top}px`, left: `${left}px` },
  }
}

export default function SeatmapTutorial({ isOpen, onClose, onComplete }) {
  const steps = getSeatmapTutorialSteps()
  const [stepIndex, setStepIndex] = useState(0)
  const [targetRect, setTargetRect] = useState(null)

  const step = steps[stepIndex]
  const isFirst = stepIndex === 0
  const isLast = stepIndex === steps.length - 1

  const updateTarget = useCallback(() => {
    if (!isOpen || !step?.target) {
      setTargetRect(null)
      return
    }
    setTargetRect(getTargetRect(step.target))
  }, [isOpen, step?.target])

  const handleFinish = useCallback(() => {
    onComplete?.()
    onClose()
  }, [onClose, onComplete])

  useLayoutEffect(() => {
    if (!isOpen) return
    updateTarget()
    window.addEventListener('resize', updateTarget)
    window.addEventListener('scroll', updateTarget, true)
    return () => {
      window.removeEventListener('resize', updateTarget)
      window.removeEventListener('scroll', updateTarget, true)
    }
  }, [isOpen, stepIndex, updateTarget])

  useEffect(() => {
    if (!isOpen) setStepIndex(0)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleFinish()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, handleFinish])

  if (!isOpen || !step) return null

  const cardPos = getCardPosition(targetRect, step.placement)

  const handleNext = () => {
    if (isLast) {
      handleFinish()
      return
    }
    setStepIndex((i) => i + 1)
  }

  const handleBack = () => {
    if (!isFirst) setStepIndex((i) => i - 1)
  }

  return createPortal(
    <div
      className="seatmap-tutorial"
      role="dialog"
      aria-modal="true"
      aria-labelledby="seatmap-tutorial-title"
    >
      {!targetRect && (
        <div className="seatmap-tutorial__backdrop" aria-hidden="true" />
      )}
      {targetRect && (
        <div
          className="seatmap-tutorial__spotlight"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
          aria-hidden="true"
        />
      )}
      <div
        className={`seatmap-tutorial__card ${cardPos.className}`}
        style={cardPos.style}
      >
        <h2 id="seatmap-tutorial-title" className="seatmap-tutorial__title">
          {step.title}
        </h2>
        <p className="seatmap-tutorial__body">{step.body}</p>
        <div className="seatmap-tutorial__footer">
          <span className="seatmap-tutorial__progress">
            {stepIndex + 1} of {steps.length}
          </span>
          <div className="seatmap-tutorial__actions">
            {!isFirst && (
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                Back
              </Button>
            )}
            {!isLast && (
              <Button
                variant="link"
                size="sm"
                className="text-muted"
                onClick={handleFinish}
              >
                Skip tour
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={handleNext}>
              {isLast ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
